import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme, VODControlsTestIDs } from '@constants';
import { useNodeHandle } from '@hooks';

import { isAndroid, isTizen, isWeb } from '../../helpers/Platform';
import { FOCUS_NOW } from '../../helpers/Tools';
import { TWCButton } from '../focusable/TWCButton/TWCButton';
import { TWCText } from '../primitives/TWCText/TWCText';

const tPath = 'videoControls.';

export const NextVideoOverlay = ({
  nextTitle,
  nextDuration,
  countdown,
  goToNextVideo,
  shouldShow,
  overlayDuration,
}) => {
  const formattedDuration = () => {
    const minutes = Math.floor(nextDuration / 60);
    const seconds = Math.floor(nextDuration % 60);

    return `${minutes}m ${seconds}s`;
  };

  const playNextReference = useRef();
  const playNextHandle = useNodeHandle(playNextReference);
  const noFocusHandle = isWeb ? null : playNextHandle;

  const { t } = useTranslation();

  useEffect(() => {
    if (shouldShow) {
      setTimeout(
        () => {
          FOCUS_NOW(playNextReference);
        },
        isAndroid ? 10 : 0,
      );
    }
  }, [shouldShow]);

  const hideDisplay = { display: 'none' };

  return (
    <View style={styles.background}>
      <View style={[styles.wrapper, shouldShow ? null : hideDisplay]}>
        <TWCText style={styles.topText} testID={VODControlsTestIDs.playNextTitle} variant={'mediumText36'}>
          {shouldShow ? t(`${tPath}upNext`) : ''}
        </TWCText>
        <TWCText
          numberOfLines={1}
          style={styles.bottomText}
          testID={VODControlsTestIDs.playNextSubtitle}
          variant={'mediumText36'}
        >{`${nextTitle} - ${formattedDuration()}`}</TWCText>
        <View style={styles.progressContainer}>
          <TWCButton
            accessibilityHint={t(`${tPath}nextUpAccessibilityHint`, {
              seconds: overlayDuration,
            })}
            accessibilityLabel={t(`${tPath}nextUpAccessibilityLabel`, {
              title: nextTitle,
            })}
            countDownTitle={t(`${tPath}nextUpCountdownTitle`, {
              seconds: countdown,
            })}
            countdownSecs={overlayDuration}
            nextFocusDown={noFocusHandle}
            nextFocusLeft={noFocusHandle}
            nextFocusRight={noFocusHandle}
            nextFocusUp={noFocusHandle}
            ref={playNextReference}
            shouldUseContextReference={false}
            testID={VODControlsTestIDs.playNextButton}
            title={t(`${tPath}nextUpTitle`)}
            onPress={goToNextVideo}
            onRenderCountDown={shouldShow}
          />
        </View>
      </View>
    </View>
  );
};

NextVideoOverlay.propTypes = {
  nextTitle: PropTypes.string,
  nextDuration: PropTypes.string,
  countdown: PropTypes.number,
  goToNextVideo: PropTypes.func.isRequired,
  shouldShow: PropTypes.bool.isRequired,
  overlayDuration: PropTypes.number,
};

NextVideoOverlay.defaultProps = {
  nextTitle: '',
  nextDuration: '',
  countdown: null,
  overLayDuration: 8,
};

const styles = StyleSheet.create({
  background: {
    bottom: Theme.px(53),
    elevation: 200,
    height: Theme.px(298),
    position: isWeb && !isTizen ? 'fixed' : 'absolute',
    right: Theme.px(46),
    width: Theme.px(632),
    zIndex: 200,
  },
  bottomText: {
    paddingTop: Theme.px(8),
  },
  progressContainer: {
    justifyContent: 'center',
    marginTop: Theme.px(50),
    width: Theme.px(422),
  },
  topText: {
    paddingTop: Theme.px(31),
  },
  wrapper: {
    alignItems: 'center',
    backgroundColor: Theme.withOpacity(Theme.blackPearlBlack, 0.8),
    borderRadius: Theme.px(12),
    height: Theme.px(298),
    width: Theme.px(632),
  },
});
